import { useCallback } from "react";

import { ApolloError, useMutation } from "@apollo/client";

import {
  ADD_DASHBOARD,
  DASHBOARDS,
  WORKSPACE,
  WORKSPACES,
  WorkspaceClientName
} from "api/workspace";

import { Dashboard, DashboardInput, Workspace } from "models/workspace";

import { HIGHLIGHT_ANIMATION_DURATION } from "../constants/highlight.constants";
import {
  CloneContext,
  calculateCloneDashboardPosition,
  createCloneContext
} from "../utils/dashboardPosition";
import { getSessionData } from "../utils/getSessionData";

interface UseCloneDashboardParamsT {
  /**
   * Called when dashboard is successfully cloned
   * @param dashboardId - ID of the newly created dashboard
   * @param cloneContext - Context information (only provided when highlightConfig is used)
   */
  onSuccess?: (dashboardId: string, cloneContext?: CloneContext) => void;
  /** Called when dashboard cloning fails */
  onError?: (error: string) => void;
  /**
   * Highlighting and scrolling configuration - when provided, enables highlight/scroll behavior
   * If not provided, dashboard will be cloned without any visual feedback
   */
  highlightConfig?: {
    /** Callback to set which dashboard should be highlighted */
    setHighlightedDashboard: (dashboardId: string | null) => void;
    /** Customization options for scrolling and highlighting behavior (defaults: both true) */
    options?: { isScrolling?: boolean; isHighlighting?: boolean };
  };
}

export interface UseCloneDashboardReturnT {
  mutate: (
    dashboardToClone: Dashboard | null,
    newDashboardTitle: string,
    newDashboardType: string,
    workspace: Workspace | null | undefined
  ) => Promise<string> | undefined;
  isLoading: boolean;
}

export function useCloneDashboard({
  onSuccess,
  onError,
  highlightConfig
}: UseCloneDashboardParamsT = {}) {
  const [addDashboard, { loading: isLoading }] = useMutation(ADD_DASHBOARD);

  const mutate = useCallback(
    async (
      dashboardToClone: Dashboard | null,
      newDashboardTitle: string,
      newDashboardType: string,
      workspace: Workspace | null | undefined
    ) => {
      if (!dashboardToClone || !workspace) return;

      // Capture the original dashboard for context creation
      const originalDashboard = dashboardToClone;
      const originalWorkspace = workspace;

      const newDashboard: DashboardInput = {
        parentId: dashboardToClone.dashboardId,
        title: newDashboardTitle,
        type: newDashboardType,
        layout: {
          format: dashboardToClone.layout.format,
          width: dashboardToClone.layout.width,
          height: dashboardToClone.layout.height
        },
        widgets: dashboardToClone.widgets.map((w) => {
          const sessionData = getSessionData(w);
          return {
            title: w.title,
            type: w.type,
            x: w.x,
            y: w.y,
            width: w.width,
            height: w.height,
            settings: sessionData || w.settings
          };
        })
      };

      // Calculate positioning using utility function
      const { position } = calculateCloneDashboardPosition(dashboardToClone, workspace);

      const variables: {
        dashboard: DashboardInput;
        workspaceId?: string;
        position?: number;
      } = {
        dashboard: newDashboard
      };

      // Always send position and workspace info since we're always adding to selected dashboards
      variables.position = position;
      variables.workspaceId = workspace.workspaceId;

      try {
        const { data } = await addDashboard({
          variables,
          context: { clientName: WorkspaceClientName },
          refetchQueries: [
            { query: DASHBOARDS, context: { clientName: WorkspaceClientName } },
            { query: WORKSPACES, context: { clientName: WorkspaceClientName } },
            {
              query: WORKSPACE,
              variables: { id: workspace.workspaceId },
              context: { clientName: WorkspaceClientName }
            }
          ],
          awaitRefetchQueries: true // Wait for refetch to complete for highlighting
        });

        const newDashboardId = data.addDashboard.dashboardId;

        // Handle highlighting and success callbacks
        if (highlightConfig) {
          const cloneContext = createCloneContext(
            originalDashboard,
            newDashboardId,
            originalWorkspace,
            highlightConfig.options
          );

          // Handle highlighting with state management
          if (cloneContext.isHighlighting) {
            highlightConfig.setHighlightedDashboard(newDashboardId);

            // Auto-clear highlight after complete animation cycle
            setTimeout(() => {
              highlightConfig.setHighlightedDashboard(null);
            }, HIGHLIGHT_ANIMATION_DURATION);
          }

          onSuccess?.(newDashboardId, cloneContext);
        } else {
          // No highlight config provided, just call onSuccess with dashboard ID only
          onSuccess?.(newDashboardId);
        }

        return newDashboardId;
      } catch (error) {
        if (error instanceof ApolloError) {
          // eslint-disable-next-line no-console
          console.error(error.message, error);
          onError?.(error.message);
        }
        throw error;
      }
    },
    [addDashboard, onSuccess, onError, highlightConfig]
  );

  return { mutate, isLoading };
}
